import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  User,
  Building2,
  Briefcase,
  Mail,
  Phone,
  Edit3,
  Check,
  Share2,
  AtSign,
} from "lucide-react";
import { motion } from "motion/react";
import { useAppStore } from "../store/useAppStore";
import { maxPlatform } from "../services/maxPlatform";
import { PageHeader } from "../components/PageHeader";
import { PageShell } from "../components/PageShell";
import { GlassCard } from "../components/GlassCard";
import { QrCodeImage } from "../components/QrCodeImage";
import { shareOrCopy } from "../components/ShareQrCarousel";
import { useTranslation } from "react-i18next";
import { isApiEnabled } from "../api/config";
import { visitorSessionService } from "../services/visitorSessionService";
import { printableQr } from "../utils/publicQr";

export function BusinessCardPage() {
  const { t } = useTranslation();
  const {
    user,
    updateUser,
    eventRole,
    selectedEventId,
    exhibitorCompanyName,
    exhibitorBadgeQrCode,
    setEventRole,
    setNavbarVisible,
  } = useAppStore();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [shareHint, setShareHint] = useState<"shared" | "copied" | null>(null);

  const isExhibitor = eventRole === "exhibitor";
  const badgeUrl = useMemo(() => {
    const opaque = exhibitorBadgeQrCode?.trim() || "";
    return opaque ? printableQr(opaque) : "";
  }, [exhibitorBadgeQrCode]);

  const [form, setForm] = useState({
    fullName: user.fullName,
    position: user.position || "",
    email: user.email || "",
    phone: user.phone || "",
    tgUsername: user.tgUsername || "",
  });

  useEffect(() => {
    const pane = document.querySelector<HTMLElement>("[data-app-scroll]");
    if (pane) pane.scrollTop = 0;
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  useEffect(() => {
    if (editing) return;
    setForm({
      fullName: user.fullName,
      position: user.position || "",
      email: user.email || "",
      phone: user.phone || "",
      tgUsername: user.tgUsername || "",
    });
  }, [user, editing]);

  const blurTimer = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (blurTimer.current) window.clearTimeout(blurTimer.current);
      setNavbarVisible(true);
    };
  }, [setNavbarVisible]);

  const onFieldFocus = () => {
    if (blurTimer.current) window.clearTimeout(blurTimer.current);
    setNavbarVisible(false);
  };
  const onFieldBlur = () => {
    blurTimer.current = window.setTimeout(() => setNavbarVisible(true), 180);
  };

  if (!isExhibitor) {
    return (
      <>
        <PageHeader title={t("visitor.my_card")} back backTo="/my" />
        <PageShell>
          <p className="mt-10 text-center text-[13px] text-gray-400 px-6">
            {t("profile.card_visitor_only")}
          </p>
        </PageShell>
      </>
    );
  }

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isApiEnabled() && selectedEventId) {
        const patched = await visitorSessionService.updateExhibitorProfile(selectedEventId, {
          fullName: form.fullName,
          position: form.position,
          email: form.email,
          phone: form.phone,
          tgUsername: form.tgUsername,
        });
        updateUser(patched.user);
        await visitorSessionService.syncEventRole(selectedEventId, setEventRole);
      } else {
        updateUser({
          ...form,
          companyName: exhibitorCompanyName || user.companyName,
        });
      }
      setEditing(false);
      maxPlatform.hapticFeedback("success");
    } catch {
      maxPlatform.showAlert(t("visitor.loading_error"));
    } finally {
      setSaving(false);
    }
  };

  const handleShare = async () => {
    maxPlatform.hapticFeedback("medium");
    if (!badgeUrl) {
      maxPlatform.showAlert(t("visitor.share_unavailable"));
      return;
    }
    const text = [
      user.fullName,
      user.position,
      exhibitorCompanyName || user.companyName,
      user.phone,
      user.email,
    ]
      .filter(Boolean)
      .join("\n");
    const result = await shareOrCopy(text, badgeUrl);
    if (result === "fail") {
      maxPlatform.showAlert(t("visitor.share_unavailable"));
      return;
    }
    setShareHint(result === "copied" ? "copied" : "shared");
    window.setTimeout(() => setShareHint(null), 1800);
  };

  return (
    <>
      <PageHeader
        title={t("visitor.my_card")}
        back
        backTo="/my"
        right={
          <button
            type="button"
            disabled={saving}
            onClick={() => (editing ? void handleSave() : setEditing(true))}
            className="flex items-center gap-1 text-[14px] text-indigo-500 active:text-indigo-700 disabled:opacity-50"
          >
            {editing ? (
              <>
                <Check size={16} />
                {t("visitor.done")}
              </>
            ) : (
              <>
                <Edit3 size={14} />
                {t("visitor.edit")}
              </>
            )}
          </button>
        }
      />
      <PageShell>
        <div className="mt-4 space-y-5">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5"
          >
            <p className="text-[13px] text-gray-500 text-center mb-3">
              {t("profile.card_qr_hint")}
            </p>
            {badgeUrl ? (
              <div className="flex justify-center">
                <QrCodeImage value={badgeUrl} size={220} className="!rounded-2xl !border-gray-100" />
              </div>
            ) : (
              <div className="h-[220px] rounded-2xl bg-gray-50 border border-dashed border-gray-200 flex items-center justify-center text-[13px] text-gray-400">
                {t("visitor.loading_error")}
              </div>
            )}
            <p className="mt-4 text-center text-[17px] text-gray-900 font-medium">{user.fullName}</p>
            {user.position ? (
              <p className="text-center text-[13px] text-gray-500 mt-0.5">{user.position}</p>
            ) : null}
            {(exhibitorCompanyName || user.companyName) && (
              <p className="text-center text-[12px] text-violet-600 mt-1">
                {exhibitorCompanyName || user.companyName}
              </p>
            )}
          </motion.div>

          <button
            type="button"
            onClick={() => void handleShare()}
            className="w-full h-11 rounded-xl bg-indigo-600 text-white text-[14px] flex items-center justify-center gap-2 active:bg-indigo-700 transition-colors"
          >
            {shareHint ? <Check size={16} /> : <Share2 size={16} />}
            {shareHint === "copied" ? t("common.copied") : t("visitor.share_card")}
          </button>

          {editing && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <GlassCard variant="default">
                <h3 className="text-[14px] text-gray-900 mb-3">{t("visitor.editing")}</h3>
                <div className="space-y-3">
                  <Field
                    icon={<User size={16} />}
                    label={t("visitor.full_name")}
                    value={form.fullName}
                    onChange={(v) => setForm({ ...form, fullName: v })}
                    onFocus={onFieldFocus}
                    onBlur={onFieldBlur}
                  />
                  <div>
                    <label className="text-[11px] text-gray-400 mb-1 block">{t("visitor.company")}</label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                        <Building2 size={16} />
                      </div>
                      <input
                        disabled
                        value={exhibitorCompanyName || user.companyName || ""}
                        className="w-full h-10 pl-10 pr-3 rounded-xl bg-gray-100 text-[14px] text-gray-500 outline-none"
                      />
                    </div>
                  </div>
                  <Field
                    icon={<Briefcase size={16} />}
                    label={t("visitor.position")}
                    value={form.position}
                    onChange={(v) => setForm({ ...form, position: v })}
                    onFocus={onFieldFocus}
                    onBlur={onFieldBlur}
                  />
                  <Field
                    icon={<Mail size={16} />}
                    label="Email"
                    value={form.email}
                    onChange={(v) => setForm({ ...form, email: v })}
                    type="email"
                    onFocus={onFieldFocus}
                    onBlur={onFieldBlur}
                  />
                  <Field
                    icon={<Phone size={16} />}
                    label={t("visitor.phone")}
                    value={form.phone}
                    onChange={(v) => setForm({ ...form, phone: v })}
                    type="tel"
                    onFocus={onFieldFocus}
                    onBlur={onFieldBlur}
                  />
                  <Field
                    icon={<AtSign size={16} />}
                    label="Telegram"
                    value={form.tgUsername}
                    onChange={(v) => setForm({ ...form, tgUsername: v })}
                    onFocus={onFieldFocus}
                    onBlur={onFieldBlur}
                  />
                </div>
              </GlassCard>
            </motion.div>
          )}

          {!editing && (
            <GlassCard variant="subtle">
              <h3 className="text-[14px] text-gray-900 mb-3">{t("visitor.card_data")}</h3>
              <div className="space-y-2.5">
                <InfoRow icon={<User size={15} />} label={t("visitor.name")} value={user.fullName} />
                <InfoRow
                  icon={<Building2 size={15} />}
                  label={t("visitor.company")}
                  value={exhibitorCompanyName || user.companyName || "—"}
                />
                <InfoRow
                  icon={<Briefcase size={15} />}
                  label={t("visitor.position")}
                  value={user.position || "—"}
                />
                <InfoRow icon={<Mail size={15} />} label="Email" value={user.email || "—"} />
                <InfoRow
                  icon={<Phone size={15} />}
                  label={t("visitor.phone")}
                  value={user.phone || "—"}
                />
                <InfoRow
                  icon={<AtSign size={15} />}
                  label="Telegram"
                  value={user.tgUsername ? `@${user.tgUsername}` : "—"}
                />
              </div>
            </GlassCard>
          )}
        </div>
      </PageShell>
    </>
  );
}

function Field({
  icon,
  label,
  value,
  onChange,
  type = "text",
  onFocus,
  onBlur,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  onFocus?: () => void;
  onBlur?: () => void;
}) {
  return (
    <div>
      <label className="text-[11px] text-gray-400 mb-1 block">{label}</label>
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">{icon}</div>
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={(e) => {
            onFocus?.();
            const el = e.currentTarget;
            window.setTimeout(() => {
              el.scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" });
            }, 80);
          }}
          onBlur={() => onBlur?.()}
          className="w-full h-10 pl-10 pr-3 rounded-xl bg-gray-50 text-[14px] text-gray-900 outline-none focus:ring-2 focus:ring-indigo-200 transition-shadow"
        />
      </div>
    </div>
  );
}

function InfoRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <div className="text-gray-400">{icon}</div>
      <div>
        <p className="text-[11px] text-gray-400">{label}</p>
        <p className="text-[14px] text-gray-900">{value}</p>
      </div>
    </div>
  );
}
